import React, { useState, useEffect } from "react";
import Plot from "react-plotly.js";
import { Grid, Typography, Paper, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useBatteryConfig } from "../context/BatteryConfigContext";
import { StyledButton, StyledTypography } from "../styles/styles";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const VisualizeData = () => {
    const [plotData, setPlotData] = useState({
        voltageCurrentTime: null,
        moduleVoltageTime: null,
        moduleTemperatureTime: null,
    });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const { selectedConfig } = useBatteryConfig();
    const navigate = useNavigate();

    useEffect(() => {
        if (!selectedConfig) return;

        const fetchPlotData = async () => {
            if (!selectedConfig.uploadData) {
                setError("No file selected");
                setLoading(false);
                return;
            }

            const formData = new FormData();
            formData.append('file', selectedConfig.uploadData);

            try {
                const response = await axios.post(`${process.env.BACKEND_URL}batteries/battery_pack_plot_v2`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
                const { voltageCurrentTime, moduleVoltageTime, moduleTemperatureTime } = response.data;

                if (!voltageCurrentTime || !moduleVoltageTime || !moduleTemperatureTime) {
                    throw new Error("Plot data is incomplete or incorrect.");
                }

                setPlotData({
                    voltageCurrentTime,
                    moduleVoltageTime,
                    moduleTemperatureTime,
                });
                setError("");
                setLoading(false);
            } catch (error) {
                setError(error.message || "Error fetching plot data. Please check the data and try again.");
                setLoading(false);
            }
        };

        fetchPlotData();
    }, [selectedConfig]);

    const handleNavigateToSubmit = () => {
        navigate("/batteryscope/submit");
    };

    const handleNavigateToDigitalTwin = () => {
        navigate("/batteryscope/digital-twin");
    };

    const plotStyle = { width: "300px", height: "200px" };

    return (
        <div className="visualize-data" style={{ textAlign: "center", padding: 20 }}>
            {!selectedConfig ? (
                <div className="mt-4">
                    <StyledTypography variant="h6" sx={{ color: "#e8442d" }}>
                        No Battery Data Selected
                    </StyledTypography>
                    <Typography variant="body1" gutterBottom className="mt-2 mb-4">
                        To visualize battery data, please submit your battery configuration first.
                    </Typography>
                    <StyledButton variant="contained" color="primary" onClick={handleNavigateToSubmit}>
                        Go to Submit Page
                    </StyledButton>
                </div>
            ) : loading ? (
                <CircularProgress />
            ) : (
                <>
                    {/* <StyledTypography variant="h6" sx={{ color: "#e8442d" }}>
                        Visualize Battery Data
                    </StyledTypography> */}
                    {error ? (
                        <>
                            <Typography variant="body1" color="error" gutterBottom>
                                {error}
                            </Typography>
                            <StyledButton variant="contained" color="primary" onClick={handleNavigateToSubmit}>
                                Go to Submit Page
                            </StyledButton>
                        </>
                    ) : (
                        <div className="flex flex-col" style={{height: "100vh"}} >
                            <div style={{ overflowY: "auto", height: "80vh", width: "100%" }}>
                                <div className="flex flex-col w-full h-full gap-4">
                                    <div className="flex flex-col p-4 max-w-[100wh] max-h-[1100px] justify-center items-center">
                                    {plotData.voltageCurrentTime && (
                                        <Plot
                                            data={plotData.voltageCurrentTime.data}
                                            layout={{
                                                ...plotData.voltageCurrentTime.layout,
                                                autosize: true,
                                            }}
                                            style={{ width: '100%', height: '100%' }} 
                                        />
                                    )}
                                    </div>
                                    <div className="flex flex-col p-4 max-w-[100wh] max-h-[1100px] justify-center items-center">
                                        {plotData.moduleVoltageTime && (
                                            <Plot
                                                data={plotData.moduleVoltageTime.data}
                                                layout={{
                                                    ...plotData.moduleVoltageTime.layout,
                                                    autosize: true,
                                                }}
                                                style={{ width: "100%", height: "100%" }}
                                            />
                                        )}
                                    </div>
                                    <div className="flex flex-col p-4 max-w-[100wh] max-h-[1100px] justify-center items-center">
                                        {plotData.moduleTemperatureTime && (
                                            <Plot
                                                data={plotData.moduleTemperatureTime.data}
                                                layout={{
                                                    ...plotData.moduleTemperatureTime.layout,
                                                    autosize: true,
                                                }}
                                                style={{ width: "100%", height: "100%" }}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4 flex justify-end">
                                {/* <Typography variant="body1" gutterBottom className="mt-2 mr-2" sx={{ color: "#e8442d", fontFamily: "Bai Jamjuree" }}>
                                    Click on the plots to zoom in/out.
                                </Typography> */}
                                <StyledButton variant="contained" onClick={handleNavigateToDigitalTwin} className="gap-2">
                                    Go to Digital Twin <ArrowForwardIcon />
                                </StyledButton>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default VisualizeData;
